import React, { useEffect } from 'react'
import { Col, Container, Form, Row } from 'react-bootstrap'
import { Controller, useForm } from 'react-hook-form'
import * as Sentry from '@sentry/react'

import { useGetAccountQuery, useUpdateAccountMutation } from '../../../store/accountUserSettings/accountUserSettings'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useAppSelector } from '../../../store/hooks'

import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import PrizeBadge from '../../../components/ScopedBlock/PrizeBadge.svg?url'
import { ScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { useZocksQuota } from '../../../components/ScopedBlock/hooks'
import { LinkButton } from '../../../components/Button/LinkButton'
import { Button } from '../../../components/Button/Button'

import { AccountConfig } from '../../../models/Account'

export const ComplianceMessageSettings: React.FC = () => {
  const accountId = useAppSelector(selectAccountId)

  const { hasQuota } = useZocksQuota()

  const complianceMessageEnabled = hasQuota('ai.complianceMessage.value')

  const { data: account, isFetching: accountFetching } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: true })

  const [updateAccount, { isLoading: accountUpdateLoading }] = useUpdateAccountMutation()

  const updateAccountConfig = async (accountConfigUpdate: Partial<AccountConfig>) => {
    if (!account) {
      return
    }
    try {
      await updateAccount({
        accountId,
        payload: {
          config: {
            ...account.data.config,
            ...accountConfigUpdate,
          },
        },
      })
    } catch (e) {
      Sentry.captureException(e)
    }
  }

  if (!account?.data) {
    return null
  }

  const { complianceConfig: effectiveComplianceConfig } = account.data.effectiveConfig
  const { complianceConfig: enterpriseComplianceConfig } = account.data.enterpriseConfig

  const {
    pdfComplianceMessage: effectivePdfComplianceMessage,
    emailComplianceMessage: effectiveEmailComplianceMessage,
    integrationExportComplianceMessage: effectiveIntegrationExportComplianceMessage,
    copyComplianceMessage: effectiveCopyComplianceMessage,
  } = effectiveComplianceConfig

  const {
    pdfComplianceMessage: enterprisePdfComplianceMessage,
    emailComplianceMessage: enterpriseEmailComplianceMessage,
    integrationExportComplianceMessage: enterpriseIntegrationExportComplianceMessage,
    copyComplianceMessage: enterpriseCopyComplianceMessage,
  } = enterpriseComplianceConfig

  const { pdfComplianceMessage, emailComplianceMessage, integrationExportComplianceMessage, copyComplianceMessage } = account.data.config

  const isAnyComplianceMessageConfigured =
    !!effectivePdfComplianceMessage?.enabled ||
    !!effectiveEmailComplianceMessage?.enabled ||
    !!effectiveIntegrationExportComplianceMessage?.enabled ||
    !!effectiveCopyComplianceMessage?.enabled

  return (
    <Container fluid className='d-flex flex-column gap-3'>
      <Row>
        <Col className='p-0'>
          <div className='d-flex align-items-center gap-3 zocks-p-1.25 zocks-bg-color-primary-light rounded'>
            <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
            <span className='zocks-text-gray-900'>
              You can add disclaimer texts to your documents and emails and to synced or copied data to define responsibilities, provide clear information about
              potential inaccuracies caused by AI generated contents and reducing risks of legal disputes.
            </span>
            {!complianceMessageEnabled && !isAnyComplianceMessageConfigured ? (
              <ScopedBlock scope={{ scope: 'account:update', superScope: ['account:admin'] }} missingScopeAction={MissingScopeAction.DISABLE}>
                <LinkButton className='d-flex p-1 p-2 align-items-center text-nowrap' variant='primary' to='/account/plan'>
                  <img alt='Upgrade' src={PrizeBadge} />
                  Get feature
                </LinkButton>
              </ScopedBlock>
            ) : null}
          </div>
        </Col>
      </Row>
      {effectivePdfComplianceMessage && (complianceMessageEnabled || effectivePdfComplianceMessage.enabled) ? (
        <ComplianceMessageSettingsForm
          infoText='The disclaimer text will be added to every page of the document.'
          switchId='compliance-message-config-pdf-enabled'
          switchLabel='Add disclaimer text to PDF exports of meeting summaries'
          config={effectivePdfComplianceMessage}
          enforcedByEnterpriseConfig={!!enterprisePdfComplianceMessage}
          updateConfig={async (pdfComplianceMessageConfigUpdate: Partial<ComplianceMessageConfig>) =>
            await updateAccountConfig({ pdfComplianceMessage: { ...pdfComplianceMessage, ...pdfComplianceMessageConfigUpdate } })
          }
          disabled={accountFetching || accountUpdateLoading || !complianceMessageEnabled}
        />
      ) : null}
      {effectiveEmailComplianceMessage && (complianceMessageEnabled || effectiveEmailComplianceMessage.enabled) ? (
        <ComplianceMessageSettingsForm
          infoText='The disclaimer text in the email draft will be editable when saving as draft in an external system.'
          switchId='compliance-message-config-email-enabled'
          switchLabel='Add disclaimer text to AI generated emails'
          config={effectiveEmailComplianceMessage}
          enforcedByEnterpriseConfig={!!enterpriseEmailComplianceMessage}
          updateConfig={async (emailComplianceMessageConfigUpdate: Partial<ComplianceMessageConfig>) =>
            await updateAccountConfig({ emailComplianceMessage: { ...emailComplianceMessage, ...emailComplianceMessageConfigUpdate } })
          }
          disabled={accountFetching || accountUpdateLoading || !complianceMessageEnabled}
        />
      ) : null}
      {effectiveIntegrationExportComplianceMessage && (complianceMessageEnabled || effectiveIntegrationExportComplianceMessage.enabled) ? (
        <ComplianceMessageSettingsForm
          switchId='compliance-message-config-integration-export-enabled'
          switchLabel='Add disclaimer text to data synced to external systems'
          config={effectiveIntegrationExportComplianceMessage}
          enforcedByEnterpriseConfig={!!enterpriseIntegrationExportComplianceMessage}
          updateConfig={async (integrationExportComplianceMessageConfigUpdate: Partial<ComplianceMessageConfig>) =>
            await updateAccountConfig({
              integrationExportComplianceMessage: { ...integrationExportComplianceMessage, ...integrationExportComplianceMessageConfigUpdate },
            })
          }
          disabled={accountFetching || accountUpdateLoading || !complianceMessageEnabled}
        />
      ) : null}
      {effectiveCopyComplianceMessage && (complianceMessageEnabled || effectiveCopyComplianceMessage.enabled) ? (
        <ComplianceMessageSettingsForm
          switchId='compliance-message-config-copy-enabled'
          switchLabel='Add disclaimer text to copied data'
          config={effectiveCopyComplianceMessage}
          enforcedByEnterpriseConfig={!!enterpriseCopyComplianceMessage}
          updateConfig={async (copyComplianceMessageConfigUpdate: Partial<ComplianceMessageConfig>) =>
            await updateAccountConfig({
              copyComplianceMessage: { ...copyComplianceMessage, ...copyComplianceMessageConfigUpdate },
            })
          }
          disabled={accountFetching || accountUpdateLoading || !complianceMessageEnabled}
        />
      ) : null}
    </Container>
  )
}

interface ComplianceMessageConfig {
  enabled: boolean
  message: string
}

interface ComplianceMessageSettingsFormValues {
  message: string
}

interface ComplianceMessageSettingsFormProps {
  infoText?: string
  switchId: string
  switchLabel: string
  config: ComplianceMessageConfig
  enforcedByEnterpriseConfig: boolean
  updateConfig: (config: Partial<ComplianceMessageConfig>) => Promise<void>
  disabled?: boolean
}

const ComplianceMessageSettingsForm: React.FC<ComplianceMessageSettingsFormProps> = ({
  infoText,
  switchId,
  switchLabel,
  config,
  enforcedByEnterpriseConfig,
  updateConfig,
  disabled = false,
}) => {
  const form = useForm<ComplianceMessageSettingsFormValues>({ defaultValues: { message: '' } })

  useEffect(() => {
    const { message } = config
    if (!message) {
      return
    }

    form.reset({ message })
  }, [config, form])

  const onSubmit = async (formValues: ComplianceMessageSettingsFormValues) => {
    const { message } = formValues
    if (!message) {
      return
    }

    await updateConfig({ message })
  }

  return (
    <>
      {enforcedByEnterpriseConfig ? (
        <Row>
          <Col className='p-0'>
            <div className='d-flex align-items-center gap-3 zocks-p-1.25 zocks-bg-color-primary-light rounded'>
              <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
              <span>Disclaimer text settings are managed at the enterprise level.</span>
            </div>
          </Col>
        </Row>
      ) : null}
      <Row>
        <Col className='p-0'>
          <div className='d-flex align-items-center gap-2 py-2'>
            <ScopedBlock scope={{ scope: 'account:update', superScope: ['account:admin'] }} missingScopeAction={MissingScopeAction.DISABLE}>
              <Form.Switch
                id={switchId}
                disabled={disabled || enforcedByEnterpriseConfig}
                onChange={async (e) => updateConfig({ enabled: e.target.checked })}
                checked={config.enabled}
              />
            </ScopedBlock>
            <label htmlFor={switchId} className='paragraph-medium'>
              {switchLabel}
            </label>
          </div>
        </Col>
      </Row>
      {config.enabled ? (
        <Row>
          <Col className='p-0'>
            <div className='d-flex flex-column gap-3 p-3 rounded border zocks-border-color-gray-300'>
              {infoText ? (
                <Row>
                  <Col>
                    <div className='d-flex align-items-center gap-3 zocks-p-1.25 zocks-bg-color-primary-light rounded'>
                      <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
                      <span>{infoText}</span>
                    </div>
                  </Col>
                </Row>
              ) : null}
              <Controller
                control={form.control}
                name='message'
                render={({ field }) => (
                  <ScopedBlock scope={{ scope: 'account:update', superScope: ['account:admin'] }} missingScopeAction={MissingScopeAction.DISABLE}>
                    <Form.Control
                      disabled={disabled || enforcedByEnterpriseConfig}
                      as='textarea'
                      rows={3}
                      placeholder='Customize'
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </ScopedBlock>
                )}
              />
              <ScopedBlock scope={{ scope: 'account:update', superScope: ['account:admin'] }} missingScopeAction={MissingScopeAction.DISABLE}>
                <Button
                  className='align-self-start'
                  variant='primary'
                  onClick={form.handleSubmit(onSubmit)}
                  disabled={disabled || enforcedByEnterpriseConfig || !form.formState.isDirty}
                >
                  Save changes
                </Button>
              </ScopedBlock>
            </div>
          </Col>
        </Row>
      ) : null}
    </>
  )
}
